import { ACCOUNT_DELETED_SIGN_IN_ERROR } from "@pax/api-contract/accounts/v1alpha1";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ACCOUNT_DELETED_PATH } from "@/features/auth/account-deletion";
import type { SignInRequirement } from "@/features/auth/auth-urls";
import { SignInScreen } from "@/features/auth/components/SignInScreen";
import { clientConfigQuery, sessionQuery } from "@/features/auth/queries";
import { signInErrorMessage } from "@/features/auth/sign-in-errors";
import { safeNextPath } from "@/lib/redirects";

const optionalString = (value: unknown) => (typeof value === "string" ? value : undefined);

export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: "Sign in · Pax.co" }] }),
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    next?: string;
    requires?: SignInRequirement;
    error?: string;
    error_description?: string;
  } => ({
    next: optionalString(search.next),
    requires: search.requires === "account" ? "account" : undefined,
    error: optionalString(search.error),
    error_description: optionalString(search.error_description),
  }),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    // Signing in with a deleted account's email shows that it was deleted, and when.
    if (search.error === ACCOUNT_DELETED_SIGN_IN_ERROR) {
      const times = new URLSearchParams(search.error_description);
      throw redirect({
        to: ACCOUNT_DELETED_PATH,
        search: {
          requested: times.get("requested") ?? undefined,
          deleted: times.get("deleted") ?? undefined,
        },
        replace: true,
      });
    }
    const session = await queryClient.query(sessionQuery);
    const next = safeNextPath(search.next);
    // A guest doesn't sign in; for a page that needs an account, they create one instead.
    if (session?.user.isAnonymous && search.requires === "account") {
      throw redirect({ to: "/create-account", search: { next }, replace: true });
    }
    if (session) throw redirect({ href: next, replace: true });
  },
  loader: ({ context: { queryClient } }) => queryClient.query(clientConfigQuery),
  component: LoginPage,
});

function LoginPage() {
  const { next, requires, error } = Route.useSearch();
  const methods = Route.useLoaderData();
  return (
    <SignInScreen
      methods={methods}
      next={safeNextPath(next)}
      requires={requires}
      notice={signInErrorMessage(error)}
    />
  );
}
